import { GhReason } from "@kwp/contracts";
/** The first header with this name (case-insensitive), trimmed; null when absent. */
export function headerValue(headers, name) {
    const lower = name.toLowerCase();
    const h = headers.find((x) => x.name.toLowerCase() === lower);
    return h === undefined ? null : h.value.trim();
}
function headerValues(headers, name) {
    const lower = name.toLowerCase();
    return headers.filter((x) => x.name.toLowerCase() === lower).map((x) => x.value);
}
// GitHub's documented notification headers (docs.github.com, "Email notification headers").
const GITHUB_FROM = "notifications@github.com";
const REPO = /^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/;
const REASON_ADDRESS = /([a-z_]+)@noreply\.github\.com/gi;
/** `[owner/repo] Title (PR #123)`, `(Issue #123)`, `(Discussion #12)`, or a bare `(#123)`. */
const SUBJECT_REPO = /\[([A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+)\]/;
const SUBJECT_ITEM = /\((PR|Issue|Discussion) #(\d+)\)\s*$/i;
const SUBJECT_NUMBER = /\(#(\d+)\)\s*$/;
/** `<owner/repo/pull/123/c456@github.com>`: the path after the repository says what it is about. */
const MESSAGE_ID_PATH = /^<?([A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+)\/([a-z_-]+)(?:\/([^/@>]+))?[^@]*@github\.com>?$/i;
const PATH_ITEMS = {
    pull: "pr",
    issues: "issue",
    discussions: "discussion",
    releases: "release",
    commit: "commit",
    commits: "commit",
};
const SUBJECT_ITEMS = { pr: "pr", issue: "issue", discussion: "discussion" };
const REASONS = new Set(GhReason.options);
function positive(text) {
    if (text === undefined || !/^\d+$/.test(text))
        return null;
    const n = Number(text);
    return n > 0 && Number.isSafeInteger(n) ? n : null;
}
/** `List-Id: owner/repo <repo.owner.github.com>` names the repository. */
function listRepo(headers) {
    const listId = headerValue(headers, "List-Id");
    if (listId === null)
        return null;
    const name = listId.split("<")[0]?.trim();
    return name !== undefined && REPO.test(name) ? name : null;
}
/** The reasons: each `<reason>@noreply.github.com` among the Cc addresses, in GitHub's vocabulary. */
export function githubReasons(headers) {
    const found = new Set();
    for (const cc of headerValues(headers, "Cc")) {
        for (const m of cc.matchAll(REASON_ADDRESS)) {
            const local = m[1].toLowerCase();
            if (REASONS.has(local))
                found.add(local);
        }
    }
    return GhReason.options.filter((r) => found.has(r));
}
/** What a Message-ID (or the In-Reply-To it answers) says: the repository, the item and its number. */
function fromMessageId(headers) {
    for (const name of ["Message-ID", "In-Reply-To"]) {
        const value = headerValue(headers, name);
        const m = value === null ? null : MESSAGE_ID_PATH.exec(value);
        if (m === null)
            continue;
        const item = PATH_ITEMS[m[2].toLowerCase()];
        if (item === undefined)
            return { repo: m[1], item: "other", number: null };
        return { repo: m[1], item, number: item === "commit" ? null : positive(m[3]) };
    }
    return null;
}
/** The repository: from `List-Id`, else the subject's `[owner/repo]`, else the Message-ID path. */
function repoOf(headers, subject, byId) {
    const listed = listRepo(headers);
    if (listed !== null)
        return listed;
    const inSubject = SUBJECT_REPO.exec(subject);
    if (inSubject !== null)
        return inSubject[1];
    return byId === null ? null : byId.repo;
}
/**
 * GitHub's `SourceInfo`. The item and its number come from the subject and, where the subject does
 * not say (`(#123)`, or nothing), from the Message-ID path.
 */
export function extractGitHub(headers, subject) {
    const byId = fromMessageId(headers);
    const typed = SUBJECT_ITEM.exec(subject);
    const bare = typed === null ? SUBJECT_NUMBER.exec(subject) : null;
    let item = byId === null ? "other" : byId.item;
    let number = byId === null ? null : byId.number;
    if (typed !== null) {
        item = SUBJECT_ITEMS[typed[1].toLowerCase()];
        number = positive(typed[2]);
    }
    else if (bare !== null) {
        number = positive(bare[1]);
    }
    const severity = headerValue(headers, "X-GitHub-Severity");
    const repo = repoOf(headers, subject, byId);
    return {
        app: "github",
        repo: repo === null ? null : repo.toLowerCase(),
        reasons: githubReasons(headers),
        item,
        number,
        severity: severity === null || severity === "" ? null : severity,
    };
}
export const KNOWN_APPS = [
    {
        app: "github",
        match: (_headers, from) => from === GITHUB_FROM,
        extract: extractGitHub,
    },
];
/** The known app a message comes from and what it says, or null. */
export function knownSource(headers, from, subject) {
    const lower = from.trim().toLowerCase();
    const entry = KNOWN_APPS.find((a) => a.match(headers, lower));
    return entry === undefined ? null : entry.extract(headers, subject);
}
/**
 * The GitHub groups and the reasons that place a thread in each, in precedence order: a thread
 * goes to the first group any of its reasons reaches.
 */
export const GH_GROUPS = [
    { group: "needs-me", reasons: ["review_requested", "mention", "team_mention", "assign"] },
    { group: "security", reasons: ["security_alert"] },
    { group: "conversation", reasons: ["comment", "author", "your_activity", "manual"] },
    { group: "activity", reasons: ["push", "state_change", "subscribed"] },
    { group: "ci", reasons: ["ci_activity"] },
];
/**
 * The group of a set of reasons, unioned over a thread's messages: GitHub's `mention` reason
 * sticks to a thread, so a later @mention can arrive as `comment`, and one message that needs
 * the owner keeps the thread in Needs me. A GitHub thread with no known reason is Activity.
 */
export function ghGroupOf(reasons) {
    const found = GH_GROUPS.find((g) => g.reasons.some((r) => reasons.includes(r)));
    return found === undefined ? "activity" : found.group;
}
//# sourceMappingURL=sources.js.map